import { useState } from "react";
import { Button, Tooltip } from "@heroui/react";
import { Pencil } from "@phosphor-icons/react";
import { RentalContract, ContractProduct } from "shared/types/rental-contracts";
import {
  EditBasePriceModal,
  PriceChangeModal,
} from "screens/buildings/components/rentalTab/modalEditContractProducts";

// Rental products live on ledger 700001 or 700002 — the same identification the
// calculation page and applyIndexation use to find the indexable rent product.
const RENTAL_LEDGERS = [700001, 700002];

interface PriceEditButtonProps {
  contract: RentalContract;
  /** "base" edits the basishuurprijs, "current" edits the huidige huurprijs. */
  mode: "base" | "current";
  onUpdated?: (contractId: number, updatedProduct: ContractProduct) => void;
}

/**
 * Small pencil button rendered next to the price cells on the indexaties
 * overview. It opens the existing, proven price-edit modals (base price or
 * price-change) scoped to the contract's rental product, so users can adjust
 * prices without navigating to Verhuur > Units > Contractproducten bewerken.
 *
 * Reusing the modals keeps all safety rails intact (Odoo push, effective date,
 * reason, confirmations). On success the parent gets the updated product for an
 * optimistic row update — no full page reload.
 */
export const PriceEditButton = ({
  contract,
  mode,
  onUpdated,
}: PriceEditButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const rentalProduct = contract.products?.find(
    (p) => p.template && RENTAL_LEDGERS.includes(p.template.ledger),
  );

  const label =
    mode === "base"
      ? "Basishuurprijs bewerken"
      : "Huidige huurprijs bewerken";

  if (!rentalProduct) {
    return (
      <Tooltip content="Geen huurproduct gevonden om te bewerken" placement="top">
        <span className="inline-block">
          <Button
            size="sm"
            isIconOnly
            variant="light"
            className="min-w-6 h-6"
            isDisabled
          >
            <Pencil size={14} className="text-neutral-secondary-mid" />
          </Button>
        </span>
      </Tooltip>
    );
  }

  const handleProductUpdate = (updated: ContractProduct) => {
    if (contract.id != null) onUpdated?.(contract.id, updated);
  };

  return (
    <>
      <Tooltip content={label} placement="top">
        <Button
          size="sm"
          isIconOnly
          variant="light"
          className="min-w-6 h-6"
          aria-label={label}
          onPress={() => setIsOpen(true)}
        >
          <Pencil size={14} className="text-neutral-secondary-mid" />
        </Button>
      </Tooltip>

      {mode === "base" ? (
        <EditBasePriceModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          product={rentalProduct}
          onSuccess={() => {}}
          onProductUpdate={handleProductUpdate}
        />
      ) : (
        <PriceChangeModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          product={rentalProduct}
          onSuccess={() => {}}
          onProductUpdate={handleProductUpdate}
        />
      )}
    </>
  );
};
